import React from 'react';
import { useSelector } from 'react-redux';
import { bool, func, shape, string, number, object } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

// Import styles
import {
	ContinueWatchingData,
	Strap
} from 'components/views/vod/components/sliders/styles/progressBar';
import {
	Wrapper,
	BgGradientStyled,
	Content,
	StyledLink,
	IconPlay,
	Button,
	IconBasket,
	IconLock,
	Info,
	EpisodeNumber,
	Title
} from './styles';

// Import utilities
import {
	useProductAvailability,
	useVodProgress,
	useProductPrices
} from 'components/utilities';

// Import components
import useInfoContext from 'components/views/vod/views/ProductDetails/Layout/Info/context/useInfoContext';

// import helpers
import { getTypeTranslation } from 'helpers';

export default function Episode({
	isActive,
	setSelected,
	resources: { data, width, height, index }
}) {
	const { t } = useTranslation();
	const { pathname } = useLocation();

	const series = useSelector(({ movieDetails }) => movieDetails.data);
	const season = useSelector(({ tvShowSeason }) => tvShowSeason.data);

	const { setVodOnlyInPackets, setProduct } = useInfoContext();

	// check episode availability based on its own availability and its parents availability
	const isSeriesAvailable = useProductAvailability(series);
	const isSeasonAvailable = useProductAvailability(season);
	const isEpisodeAvailable = useProductAvailability(data);

	// Episode prices
	const { availablePrices } = useProductPrices(data);

	const { title, uuid: episodeUuid, number, duration } = data;

	const isEpisodeAvailableToPlay =
		isSeriesAvailable || isSeasonAvailable || isEpisodeAvailable;

	const isAvailableToBuy = !!availablePrices?.length;

	const isEpisode = pathname.includes(getTypeTranslation('series'));
	const type = isEpisode ? 'series' : 'movie';

	const handleEpisodeHover = () => setSelected(index);

	const continuePercent = useVodProgress(episodeUuid, duration);

	const handleSetProductPayment = () => {
		const [type, { price, period, promotion }] = availablePrices[0];

		setProduct({
			price,
			period,
			productName: title,
			uuid: episodeUuid,
			isPurchaseRedirect: true,
			transactionType: type,
			promotion
		});
	};

	const handleVodOnlyInPackets = () =>
		setVodOnlyInPackets(episodeUuid, 'episode');

	return (
		<Wrapper
			onMouseEnter={handleEpisodeHover}
			isActive={isActive}
			width={width}
			height={height}
		>
			{isActive && <BgGradientStyled />}
			<Content isActive={isActive} id={episodeUuid}>
				{isActive &&
					(isEpisodeAvailableToPlay ? (
						<StyledLink
							to={`/vod/ogladaj/${getTypeTranslation(type)}/${episodeUuid}`}
						>
							<IconPlay />
						</StyledLink>
					) : isAvailableToBuy ? (
						<Button onClick={handleSetProductPayment}>
							<IconBasket />
						</Button>
					) : (
						<Button onClick={handleVodOnlyInPackets}>
							<IconLock />
						</Button>
					))}
				{!isActive && (
					<Info>
						{number && (
							<EpisodeNumber>
								{t('vod_movieDetails_episode')} {number}
							</EpisodeNumber>
						)}
						<Title>{title}</Title>
					</Info>
				)}
				{!!continuePercent && isEpisodeAvailableToPlay && (
					<ContinueWatchingData>
						<Strap percent={continuePercent} />
					</ContinueWatchingData>
				)}
			</Content>
		</Wrapper>
	);
}

Episode.propTypes = {
	isActive: bool.isRequired,
	setSelected: func.isRequired,
	resources: shape({
		data: shape({
			title: string.isRequired,
			uuid: string.isRequired,
			prices: shape({
				purchase: object.isRequired,
				rent: object.isRequired
			})
		}),
		width: number.isRequired,
		height: number.isRequired,
		index: number.isRequired
	})
};
